import { Pipe, PipeTransform } from "@angular/core";
import { formatNumber } from "@angular/common";
import { parseNumber } from "../utils/number-utils.service";

export type SigRule = { minAbs: number; sig: number };

export type AutoSigOptions = {
  /** Règles (minAbs inclusif). Exemple: [{minAbs:1000,sig:3}, {minAbs:1,sig:4}, ...] */
  rules?: SigRule[];

  /** Valeur par défaut si aucune règle ne matche */
  defaultSig?: number;

  /** Clamp global */
  minSig?: number;
  maxSig?: number;

  /** Clamp sur les décimales affichées */
  minDecimals?: number;
  maxDecimals?: number;

  /** Si true: n'affiche pas les zéros finaux (minFractionDigits = 0) */
  trimTrailingZeros?: boolean;
};

const DEFAULT_MIN_DECIMALS = 2;
const DEFAULT_SIG_FOR_SMALL = 5;
// Garde-fou : sous ce seuil de décimales calculées, on considère que la valeur
// est du bruit de virgule flottante et on l'affiche comme zéro (ex: 1.4803e-17
// renvoyé par un écart-type sur des prix identiques).
const DEFAULT_MAX_DECIMALS = 8;

@Pipe({
  name: "numberSig",
  standalone: true,
  pure: true,
})
export class AutoSignificantDigitsPipe implements PipeTransform {
  transform(
    value: number | string | null | undefined,
    opts: AutoSigOptions = {},
  ): string {
    if (value === null || value === undefined) return "";

    const n =
      typeof value === "string" ? parseNumber(value.replace(",", ".")) : value;
    if (!Number.isFinite(n)) return "";

    const locale = "fr-FR";
    const minDecimals = opts.minDecimals ?? DEFAULT_MIN_DECIMALS;

    // Gestion du zéro
    if (n === 0) {
      return formatNumber(0, locale, `1.${minDecimals}-${minDecimals}`);
    }

    const abs = Math.abs(n);

    // Déterminer le nombre de décimales selon la magnitude
    let decimals: number;

    if (abs >= 100) {
      decimals = 2;
    } else if (abs >= 10) {
      decimals = 3;
    } else if (abs >= 1) {
      decimals = 4;
    } else {
      // abs < 1 : utiliser 5 chiffres significatifs
      const sig = opts.defaultSig ?? DEFAULT_SIG_FOR_SMALL;
      const exp = Math.floor(Math.log10(abs));
      decimals = Math.max(0, sig - 1 - exp);
    }

    // Clamp : au-delà de ce nombre de décimales, on est dans le bruit float.
    decimals = Math.min(decimals, opts.maxDecimals ?? DEFAULT_MAX_DECIMALS);

    // Arrondir le nombre au nombre de décimales calculé
    const factor = Math.pow(10, decimals);
    const rounded = Math.round(n * factor) / factor;

    // Après arrondi, la valeur peut être devenue 0 (bruit float).
    if (rounded === 0) {
      return formatNumber(0, locale, `1.${minDecimals}-${minDecimals}`);
    }

    // Appliquer le minimum de décimales
    const finalDecimals = Math.max(decimals, minDecimals);

    // Formater avec suppression des zéros finaux inutiles (mais min 2)
    const digitsInfo = `1.${minDecimals}-${finalDecimals}`;
    return formatNumber(rounded, locale, digitsInfo);
  }
}
